import * as Yup from 'yup';
import { useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { useFormik, Form, FormikProvider } from 'formik';
import { Icon } from '@iconify/react';
import eyeFill from '@iconify/icons-eva/eye-fill';
import eyeOffFill from '@iconify/icons-eva/eye-off-fill';
import {
    Card, Stack, Container, Typography, TextField, IconButton, InputAdornment,
    Alert, Box, Link, Divider,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { styled } from '@mui/material/styles';
import Page from 'src/components/Page';
import syndicationApi from 'src/utils/syndicationApi';

const RootStyle = styled(Page)(({ theme }) => ({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
    padding: theme.spacing(4, 0),
}));

const FormCard = styled(Card)(({ theme }) => ({
    maxWidth: 480,
    width: '100%',
    padding: theme.spacing(5),
    borderRadius: 16,
    boxShadow: '0 8px 40px rgba(0,0,0,0.12)',
}));

export default function InvestorSetPassword() {
    const { token } = useParams();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [apiError, setApiError] = useState('');
    const [success, setSuccess] = useState(false);

    const SetPasswordSchema = Yup.object().shape({
        password: Yup.string()
            .min(8, 'Minimum 8 characters')
            .matches(/[A-Z]/, 'Must contain an uppercase letter')
            .matches(/[a-z]/, 'Must contain a lowercase letter')
            .matches(/[0-9]/, 'Must contain a digit')
            .matches(/[!@#$%^&*(),.?":{}|<>]/, 'Must contain a special character')
            .required('Password is required'),
        confirm_password: Yup.string()
            .oneOf([Yup.ref('password'), null], 'Passwords must match')
            .required('Confirm your password'),
    });

    const formik = useFormik({
        initialValues: { password: '', confirm_password: '' },
        validationSchema: SetPasswordSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setApiError('');
            try {
                await syndicationApi.post('investor/set-password/', {
                    token,
                    password: values.password,
                    confirm_password: values.confirm_password,
                });
                setSuccess(true);
            } catch (error) {
                setApiError(error.response?.data?.message || 'Failed to set password. The link may be expired.');
            } finally {
                setSubmitting(false);
            }
        },
    });

    if (success) {
        return (
            <RootStyle title="Password Set">
                <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
                    <FormCard>
                        <Stack spacing={3} alignItems="center" sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" color="primary">Password Set!</Typography>
                            <Alert severity="success" sx={{ width: '100%' }}>
                                Your password has been set. Check your email for the 2FA setup link to complete account activation.
                            </Alert>
                            <Link component={RouterLink} to="/investor/login" variant="subtitle1">
                                Go to Login
                            </Link>
                        </Stack>
                    </FormCard>
                </Container>
            </RootStyle>
        );
    }

    return (
        <RootStyle title="Set Password | Gateway X Syndication">
            <Container maxWidth="sm" sx={{ display: 'flex', justifyContent: 'center' }}>
                <FormCard>
                    <Stack spacing={3}>
                        <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" gutterBottom>Set Your Password</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Welcome to Gateway X Syndication. Please set a password for your account.
                            </Typography>
                        </Box>

                        {apiError && <Alert severity="error">{apiError}</Alert>}

                        <FormikProvider value={formik}>
                            <Form autoComplete="off" noValidate onSubmit={formik.handleSubmit}>
                                <Stack spacing={2.5}>
                                    <TextField
                                        fullWidth
                                        label="Password"
                                        type={showPassword ? 'text' : 'password'}
                                        {...formik.getFieldProps('password')}
                                        error={Boolean(formik.touched.password && formik.errors.password)}
                                        helperText={formik.touched.password && formik.errors.password}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                                        <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Confirm Password"
                                        type={showConfirm ? 'text' : 'password'}
                                        {...formik.getFieldProps('confirm_password')}
                                        error={Boolean(formik.touched.confirm_password && formik.errors.confirm_password)}
                                        helperText={formik.touched.confirm_password && formik.errors.confirm_password}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end">
                                                        <Icon icon={showConfirm ? eyeFill : eyeOffFill} />
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                    <LoadingButton
                                        fullWidth
                                        size="large"
                                        type="submit"
                                        variant="contained"
                                        loading={formik.isSubmitting}
                                    >
                                        Set Password
                                    </LoadingButton>
                                </Stack>
                            </Form>
                        </FormikProvider>

                        <Divider />
                        <Link component={RouterLink} to="/investor/login" variant="body2" sx={{ textAlign: 'center' }}>
                            Back to Login
                        </Link>
                    </Stack>
                </FormCard>
            </Container>
        </RootStyle>
    );
}
